<script>
import { mapGetters } from 'vuex'

import Layout from '@layouts/main.vue'
import CalculatorSection from '@components/common/calculator-section.vue'
import ListBullet from '@assets/icons/list-bullet.svg'
import ListTick from '@assets/icons/list-tick.svg'
import StarsRating from '@components/stars-rating.vue'
import ReviewsSlider from '@components/common/sliders/reviews-slider.vue'
import PointsMap from '@components/get-your-pass/points-map.vue'
import HowToUseSection from '@components/get-your-pass/how-to-use-section.vue'
import Slider from '@components/get-your-pass/attractions-points-slider.vue'

import Translatable from '@src/mixins/translatable.js'
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js'
import ImageMixin from '@src/mixins/imageMixin.js'
import Resizable from '@src/mixins/resizable.js'
import AuthorLabel from '@src/mixins/author.js'

export default {
  page: {
    title: 'CoolPass / Prague Card',
    meta: [{ name: 'description', content: 'Download the digital QR pass straight to your mobile app or collect the classic smart card upon arrival' }],
  },
  components: {
    Layout,
    CalculatorSection,
    HowToUseSection,
    Slider,
    StarsRating,
    ReviewsSlider,
    PointsMap,
  },
  mixins: [Translatable, SanitizerMixin, ImageMixin, Resizable, AuthorLabel],
  data() {
    return {
      toggle: true,
    }
  },
  computed: {
    ...mapGetters('getYourPass', ['pageData']),
    ...mapGetters('points', ['points']),
    ...mapGetters('reviews', ['averageRating', 'fifteenRandomReviews']),
    getListStyleBullet() {
      return {
        listStyleImage: `url(${ListBullet})`,
      }
    },
    getListStyleTick() {
      return {
        listStyleImage: `url(${ListTick})`,
      }
    },
    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen
          ? this.pageData.mainImage.web_image
          : this.pageData.mainImage.app_image
      }
      return ''
    },
    pageContent() {
      if (this.pageData.content) {
        return this.pageData.content[this.currentLanguage].title
          ? this.pageData.content[this.currentLanguage]
          : this.pageData.content.en
      }
      return []
    },
  },
  methods: {
    pageContentU(prop) {
      if (this.pageData.content) {
        if (Array.isArray(this.pageData.content[this.currentLanguage][prop])) {
          return this.pageData.content[this.currentLanguage][prop].map(
            (_, i) => {
              return this.pageData.content[this.currentLanguage][prop][i].title
                ? this.pageData.content[this.currentLanguage][prop][i]
                : this.pageData.content.en[prop][i]
            }
          )
        }
        return this.pageData.content[this.currentLanguage][prop]
          ? this.pageData.content[this.currentLanguage][prop]
          : this.pageData.content.en[prop]
      }
      return this.pageData.content.en[prop]
    },
  },
}
</script>

<template>
  <Layout>
    <section class="header justify-center" :style="getBackground(pageImg)">
      <div class="header-block">
        <h1
          class="header-title"
          v-html="getTranslatedContent('title', pageData.content)"
        ></h1>
        <h3
          class="header-subtitle"
          v-html="getTranslatedContent('subtitle', pageData.content)"
        ></h3>
      </div>
    </section>
    <section class="description-banner justify-center">
      <p class="author container" v-html="authorLabel(pageData)"></p>
      <div class="description">
        <div class="description-card">
          <div class="banner-phone"></div>
          <div class="banner-content">
            <h3
              class="banner-title"
              v-html="textSanitize(pageContent.description_banner[0].title)"
            ></h3>
            <h4
              class="banner-subtitle"
              v-html="textSanitize(pageContent.description_banner[0].subtitle)"
            ></h4>
            <p
              class="banner-text"
              v-html="textSanitize(pageContent.description_banner[0].text)"
            ></p>
          </div>
        </div>
        <div class="description-card">
          <div class="banner-card"></div>
          <div class="banner-content">
            <h3
              class="banner-title"
              v-html="textSanitize(pageContent.description_banner[1].title)"
            ></h3>
            <h4
              class="banner-subtitle"
              v-html="textSanitize(pageContent.description_banner[1].subtitle)"
            ></h4>
            <p
              class="banner-text"
              v-html="textSanitize(pageContent.description_banner[1].text)"
            ></p>
          </div>
        </div>
      </div>
    </section>
    <section id="prices" class="calculator-section justify-center">
      <div class="container">
        <h3 class="section-title" v-html="pageContentU('prices_title')"> </h3>
        <h3
          class="calculator-section-subtitle"
          v-html="textSanitize(pageContentU('prices_description'))"
        >
        </h3>
        <CalculatorSection />
        <div class="tips">
          <div class="first-tip-block">
            <ul :style="getListStyleBullet" class="bullet-list">
              <li>{{ getTranslatedContent('CALCULATOR_card_validity') }}</li>
              <li>{{
                getTranslatedContent('CALCULATOR_child_card_validity_tip')
              }}</li>
              <li>{{ getTranslatedContent('CALCULATOR_student_id_info') }}</li>
            </ul>
          </div>
          <div class="second-tip-block">
            <ul :style="getListStyleBullet" class="bullet-list">
              <li>
                {{ getTranslatedContent('CALCULATOR_groups_info') }}
              </li>
            </ul>
          </div>
          <div class="third-tip-block">
            <div>
              <p>{{ getTranslatedContent('ADULT_AGE') }}</p>
              <p>{{ getTranslatedContent('STUDENT_AGE') }}</p>
              <p>{{ getTranslatedContent('CHILD_AGE') }}</p>
            </div>
            <router-link :to="`/cancellation-and-refund`" class="link">
              <button class="button-cancel">{{
                getTranslatedContent('CANCELLATION_POLICY')
              }}</button>
            </router-link>
          </div>
        </div>
      </div>
    </section>
    <section id="using-coolpass" class="steps-section justify-center">
      <div class="container">
        <h3
          class="section-title"
          v-html="getTranslatedContent('GET_YOUR_PASS_steps_title')"
        >
        </h3>
        <HowToUseSection :steps="pageContentU('steps')" />
      </div>
    </section>
    <section class="tips-section justify-center">
      <div class="container">
        <h3
          class="using-title"
          v-html="getTranslatedContent('GET_YOUR_PASS_using_coolpass_title')"
        >
        </h3>
        <div class="tips-details">
          <div class="first-tip-detail-block">
            <ul :style="getListStyleTick">
              <li>{{
                getTranslatedContent('GET_YOUR_PASS_first_using_detail')
              }}</li>
              <li>{{
                getTranslatedContent('GET_YOUR_PASS_second_using_detail')
              }}</li>
            </ul>
          </div>
          <div class="second-tip-detail-block">
            <ul :style="getListStyleTick">
              <li>
                {{ getTranslatedContent('GET_YOUR_PASS_third_using_detail') }}
              </li>
              <li>{{
                getTranslatedContent('GET_YOUR_PASS_fourth_using_detail')
              }}</li>
            </ul>
          </div>
          <div class="third-tip-detail-block">
            <ul :style="getListStyleTick">
              <li>
                {{ getTranslatedContent('GET_YOUR_PASS_fifth_using_detail') }}
              </li>
            </ul>
            <router-link :to="`/faq`" class="link">
              <button class="button-faq">{{
                getTranslatedContent('GET_YOUR_PASS_read_faq_button')
              }}</button>
            </router-link>
          </div>
        </div>
      </div>
    </section>
    <section id="how-to-get" class="how-to-get-section justify-center">
      <div class="container">
        <h3 class="section-title" v-html="pageContentU('how_to_get_title')">
        </h3>
        <div class="tips-get-card">
          <p
            class="tips-get-card-text"
            v-html="textSanitize(pageContentU('how_to_get_text'))"
          >
          </p>
        </div>
      </div>
    </section>
    <section id="sales-points" class="justify-center">
      <div class="container">
        <h3
          class="section-title"
          v-html="
            getTranslatedContent('GET_YOUR_PASS_sales_points_section_title')
          "
        >
        </h3>
      </div>
    </section>
    <section class="justify-center top-attractions-carousel">
      <Slider :points="points" />
    </section>
    <section class="justify-center map-section">
      <div class="container">
        <PointsMap :points="points" />
      </div>
    </section>
    <section class="reviews-section justify-center">
      <div class="container">
        <div class="reviews-section-header">
          <h3
            class="reviews-title"
            v-html="getTranslatedContent('REVIEWS_what_do_customers_say')"
          >
          </h3>
          <div class="rating-wrapper">
            <div class="average-review-rating">
              <div class="average-rating">
                {{ averageRating && averageRating.toFixed(1) }}
              </div>
            </div>
            <StarsRating :rating="averageRating && averageRating.toFixed(0)" />
          </div>
        </div>
      </div>
    </section>
    <section>
      <ReviewsSlider :reviews="fifteenRandomReviews" />
    </section>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/author_text.scss';

.header {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  height: 550px;
  width: 100%;
}

.header-block {
  width: 840px;
  height: 135px;
  // background: rgba(37, 44, 62, 0.36);
  padding-top: 9px;
  box-sizing: border-box;
  margin-top: 217px;
  margin-right: 300px;

  .header-title {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    margin-left: 22px;
    color: $white;
  }

  .header-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    margin-left: 25px;
    color: $white;
  }
}

.description-banner {
  margin: -99px 0 0px;
  position: relative;
  & .description {
    width: 1140px;
    height: 328px;
    display: flex;
    flex-direction: row;
    border-radius: 20px;
    box-sizing: border-box;
    background: $white;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
  }
}

.description-card {
  display: flex;
  align-items: center;
}

.banner-card {
  margin-left: 20px;
  // margin-top: 25px;
  margin-right: 20px;
  border-radius: 10px;
  height: 155px;
  width: 235px;
  background: url('../../../assets/images/prague-card-rounded.png') no-repeat
    center;
  background-size: cover;
}

.banner-phone {
  margin-top: 40px;
  height: 290px;
  width: 220px;
  border-radius: 0px 0px 0px 20px;
  background: url('../../../assets/images/phone-with-hand.png') no-repeat center;
  background-size: cover;
}

.banner-content {
  width: 300px;
}

.banner-subtitle {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 18px;
  line-height: 29px;
  text-transform: uppercase;
  color: $dark-blue;
}

.section-title,
.reviews-title,
.banner-title {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 29px;
  text-transform: uppercase;
  color: $dark-blue;
  margin-bottom: 15px;
}

.section-title {
  margin-top: 80px;
}

.using-title {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 18px;
  line-height: 23px;
  text-transform: uppercase;
  color: $black;
  margin-top: 35px;
  margin-bottom: 15px;
}

::v-deep .calculator-section-subtitle,
::v-deep .tips-get-card-text,
::v-deep .banner-text {
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  color: $black;
  user-select: none;

  & em {
    font-style: italic;
  }

  & a {
    color: $gray;
    text-decoration: none;

    &:hover {
      color: $orange;
    }
  }

  & ul {
    margin-left: 20px;
    list-style: initial;
  }

  & strong {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
  }

  & .ql-align-center {
    text-align: center;
  }
}

.link {
  display: block;
  outline: none;
  height: 48px;
}

.button-cancel {
  min-width: 175px;
  max-width: 200px;
  min-height: 48px;
  border: 1px solid $gray;
  border-radius: 5px;
  background: $white;
  color: $gray;
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  padding: 12px 20px;

  &:hover {
    border: none;
    background: $gray;
    color: $white;
  }
}

.button-faq {
  min-width: 175px;
  min-height: 48px;
  border: none;
  border-radius: 5px;
  background: $gray;
  color: $white;
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  padding: 12px 30px;
  text-transform: uppercase;
  margin-top: 20px;

  &:hover {
    border: 1px solid $gray;
    background: $white;
    color: $gray;
  }
}

.tips,
.tips-details {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 20px;
  height: auto;
}

.tips ul {
  list-style: disc outside;
  width: 353px;
}

.tips ul li {
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
  color: $black;
  // opacity: 0.8;
  margin-bottom: 10px;
  padding-left: 10px;
}

.first-tip-block,
.second-tip-block,
.first-tip-detail-block,
.second-tip-detail-block {
  display: flex;
  justify-content: flex-end;
}

.third-tip-block {
  display: flex;
  justify-content: space-between;
}

.first-tip-block,
.third-tip-block {
  margin-top: 33px;
}

.second-tip-block {
  margin-top: 43px;
}

.third-tip-block p {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 14px;
  line-height: 18px;
  color: $black;
  // opacity: 0.8;
  margin-bottom: 10px;
}

.third-tip-block p:last-child {
  margin-bottom: 0;
}

.tips-details ul {
  list-style: disc outside;
  width: 349px;
}

.tips-details ul li {
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  color: $dark-blue;
  margin-bottom: 15px;
  padding-left: 10px;
}

.third-tip-detail-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.tips-get-card {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  column-gap: 20px;
  height: auto;
}

.map-section .container {
  z-index: 1;
}

.top-attractions-carousel {
  margin-top: 15px;
  margin-bottom: 40px;
}

.reviews-section {
  margin-top: 25px;
}

.reviews-title {
  margin-top: 15px;
}

.reviews-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.rating-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
}

.average-review-rating {
  margin-left: auto;
  margin-right: 12px;
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 29px;
  color: $dark-blue;
}

@media only screen and (max-width: $screen-width-xs) {
  .main-container {
    width: 100%;
  }

  .header {
    height: 244px;
  }

  .header-block {
    width: 100%;
    margin-top: 70px;
    margin-right: 0;
    padding-top: 0;
    height: 125px;

    .header-title {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 26px;
      line-height: 34px;
      text-transform: uppercase;
      margin: 0px 8px 10px;
      color: $white;
    }

    .header-subtitle {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 21px;
      color: $white;
      margin: 0 8px;
    }
  }

  .description-banner {
    margin: -20px 0 0px;

    & .description {
      flex-direction: column;
      width: 100%;
      height: auto;
      padding: 0 8px;
    }
  }

  .description-card {
    flex-direction: column;
  }

  .banner-card,
  .banner-phone {
    margin-top: 0;
    order: 2;
  }

  .banner-phone {
    border-radius: 0px 0px 0px 40px;
    height: 166px;
    width: 147px;
    margin-bottom: 20px;
  }

  .banner-card {
    height: 116px;
    width: 174px;
    margin-left: 20px;
    margin-bottom: 20px;
  }

  .banner-content {
    text-align: center;
    width: 100%;
    order: 1;
  }

  .banner-title {
    font-size: 18px;
    line-height: 23px;
    margin: 15px 0 5px;
  }

  .banner-subtitle {
    font-size: 16px;
    line-height: 21px;
    margin-bottom: 10px;
  }

  .banner-text {
    margin-bottom: 15px;
    text-align: left;
  }

  .section-title {
    margin-top: 60px;
  }

  .calculator-section-subtitle {
    margin-bottom: 20px;
  }

  .tips,
  .tips-details,
  .tips-get-card {
    width: 100%;
    grid-template-columns: repeat(1, 1fr);
    column-gap: 0px;
  }

  .tips {
    margin-top: 15px;
  }

  .tips ul,
  .tips-details ul {
    width: 100%;
  }

  .first-tip-block,
  .second-tip-block,
  .third-tip-block {
    margin: 0 0 0 15px;
    justify-content: flex-start;
  }

  .third-tip-block {
    margin-left: 25px;
    justify-content: space-between;

    p {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 12px;
      line-height: 16px;
      color: $black;
      // opacity: 0.8;
      margin-bottom: 10px;
    }
  }

  .button-cancel {
    min-width: 142px;
    max-width: 162px;
    height: 35px;
    padding: 0;
  }

  .using-title {
    margin-top: 15px;
  }

  .tips-details ul li {
    margin-bottom: 10px;
  }

  .third-tip-detail-block {
    justify-content: space-between;

    .link {
      width: 100%;
    }
  }

  .button-faq {
    min-width: 100%;
    height: auto;
    margin-top: 20px;
  }

  .first-tip-detail-block,
  .second-tip-detail-block {
    justify-content: flex-start;
    align-items: flex-start;
    margin-left: 15px;
  }

  .third-tip-detail-block ul li {
    margin-left: 15px;
  }

  .tips-get-card-text {
    margin-bottom: 10px;
  }

  .map-section .container {
    padding: 0;
  }

  .reviews-section-header {
    flex-direction: column;
  }

  .rating-wrapper {
    margin: 0 auto 0 0;
    justify-content: start;
  }

  .average-review-rating {
    margin-right: 12px;
  }
}
</style>
